import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import type {
  AtVirtualClinicKostentraeger,
} from '@bindoc-projects/virtual-clinic-domain-lib';
import {
  AT_VIRTUAL_CLINIC_KOSTENTRAEGER_DISPLAY_NAMES
} from '@bindoc-projects/virtual-clinic-domain-lib';
import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const AT_KOSTENTRAEGER_CONDITION_ID = 'kostentraegerAT';
export const AtFilterConditionKostentraegerPluginType: FilterConditionPluginSpecification<
  AtVirtualClinicKostentraeger[],
  AtFilterConditionKostentraegerDto,
  KostentraegerATCondition
> = {
  type: 'kostentraegerAT',
  category: 'case',
  displayName: { de: 'Kostenträger' },
  sortingIndex: 12,
  occurrence: 'single',
  fromObject(
    obj: AtFilterConditionKostentraegerDto,
  ): FilterConditionPluginImpl<AtVirtualClinicKostentraeger[]> {
    return new AtFilterConditionKostentraegerPlugin(obj.kostentraeger);
  },
  fromCondition(
    jsonObj: KostentraegerATCondition,
  ): FilterConditionPluginImpl<AtVirtualClinicKostentraeger[]> {
    return new AtFilterConditionKostentraegerPlugin(jsonObj.includes ?? []);
  },
  newInstance(): FilterConditionPluginImpl<AtVirtualClinicKostentraeger[]> {
    return new AtFilterConditionKostentraegerPlugin([]);
  },
};

export class AtFilterConditionKostentraegerPlugin extends FilterConditionPluginImpl<
  AtVirtualClinicKostentraeger[],
  AtFilterConditionKostentraegerDto
> {
  static override Specification: FilterConditionPluginSpecification<
    AtVirtualClinicKostentraeger[],
    AtFilterConditionKostentraegerDto,
    KostentraegerATCondition
  > = AtFilterConditionKostentraegerPluginType;

  constructor(protected kostentraeger: AtVirtualClinicKostentraeger[]) {
    super(AtFilterConditionKostentraegerPluginType.type);
  }

  toCondition(): BdCondition {
    return new KostentraegerATCondition(this.kostentraeger, []);
  }

  toJSON(): AtFilterConditionKostentraegerDto | null {
    return {
      type: this.type,
      kostentraeger: this.kostentraeger,
    };
  }

  toText(): string | null {
    return this.kostentraeger.length === 1
      ? 'ein Kostenträger'
      : `${this.kostentraeger.length} Kostenträger`;
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = this.kostentraeger.map((key) => {
      const displayName = AT_VIRTUAL_CLINIC_KOSTENTRAEGER_DISPLAY_NAMES[key];
      return displayName ? `${key} — ${displayName}` : key;
    });
    return { title, items };
  }

  toValue(): AtVirtualClinicKostentraeger[] {
    return this.kostentraeger;
  }

  override equals(other: AtFilterConditionKostentraegerPlugin): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.kostentraeger, other.kostentraeger)
    );
  }

  override validationToText(): string {
    if (this.kostentraeger.length === 0) {
      return 'Mindestens einen Kostenträger auswählen';
    } else {
      return '';
    }
  }

  clone(): AtFilterConditionKostentraegerPlugin {
    return new AtFilterConditionKostentraegerPlugin([...this.kostentraeger]);
  }
}

export class KostentraegerATCondition extends BdContentCondition<AtVirtualClinicKostentraeger> {
  constructor(
    includes: AtVirtualClinicKostentraeger[],
    excludes: AtVirtualClinicKostentraeger[],
  ) {
    super(AT_KOSTENTRAEGER_CONDITION_ID, includes, excludes);
  }
}

interface AtFilterConditionKostentraegerDto {
  type: string;
  kostentraeger: AtVirtualClinicKostentraeger[];
}
